import { useState } from "react";
import Button from "../../../shared/components/Button";
import { TextField } from "../../../shared/components/form";
import { createCamera, discoverCameras, type CreateCameraRequest, type DiscoveryResult } from "../api";
import type { Camera } from "../model";

type CameraForm = CreateCameraRequest;

const emptyForm: CameraForm = {
  name: "",
  location: "",
  rtsp_url: "",
  onvif_url: "",
  username: "",
  password: "",
};

export function CameraModal({
  onClose,
  onCreated,
  mapCamera,
}: {
  onClose: () => void;
  onCreated: (camera: Camera) => void;
  mapCamera: (camera: Record<string, string>) => Camera;
}) {
  const [form, setForm] = useState<CameraForm>(emptyForm);
  const [target, setTarget] = useState("10.0.0.0/24");
  const [results, setResults] = useState<DiscoveryResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key: keyof CameraForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const scan = async () => {
    setScanning(true);
    setError("");
    try {
      setResults(await discoverCameras(target));
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : "Camera scan failed");
    } finally {
      setScanning(false);
    }
  };
  const save = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      onCreated(mapCamera(await createCamera(form)));
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Camera could not be added");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div aria-labelledby="add-camera-title" aria-modal="true" className="dialog-backdrop" role="dialog">
      <form className="dialog-panel dialog-panel--wide" onSubmit={save}>
        <div className="dialog-header">
          <div>
            <p className="eyebrow">Camera registry</p>
            <h2 id="add-camera-title">Add camera</h2>
            <p>Scan one host or an IPv4 subnet for RTSP and ONVIF services.</p>
          </div>
          <Button aria-label="Close add camera dialog" className="close" onClick={onClose} type="button">×</Button>
        </div>
        <div className="dialog-content">
          <section className="scanner">
            <h3>Network scanner</h3>
            <div className="scanner-row">
              <TextField aria-label="IP address or CIDR to scan" onChange={(event) => setTarget(event.target.value)} value={target} />
              <Button disabled={scanning} onClick={() => void scan()} type="button" variant="primary">
                {scanning ? "Scanning…" : "Scan"}
              </Button>
            </div>
            <p>Scan is limited to 256 IPv4 addresses. Credentials are never used during discovery.</p>
            <div className="discoveries">
              {results.length === 0 ? <p className="empty">No discoveries yet.</p> : results.map((result) => (
                <div className="discovery" key={result.host}>
                  <b>{result.host}</b>
                  <span>{result.rtsp_urls?.join(", ") || "No RTSP URL found"}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="camera-form">
            <h3>Camera details</h3>
            {(["name", "location", "rtsp_url", "onvif_url", "username", "password"] as const).map((key) => (
              <TextField
                key={key}
                label={key.replace("_", " ")}
                onChange={(event) => update(key, event.target.value)}
                required={key === "name" || key === "location" || key === "rtsp_url"}
                type={key === "password" ? "password" : "text"}
                value={form[key]}
              />
            ))}
            {error && <p className="form-error">{error}</p>}
            <div className="dialog-actions">
              <Button onClick={onClose} type="button" variant="secondary">Cancel</Button>
              <Button disabled={saving} type="submit" variant="primary">{saving ? "Saving…" : "Add camera"}</Button>
            </div>
          </section>
        </div>
      </form>
    </div>
  );
}
